import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Waves, 
  UtensilsCrossed, 
  Mountain, 
  Wifi, 
  Car,
  Beef,
  Users,
  PartyPopper,
  Heart,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import BookingWidget from '../components/BookingWidget';
import Gallery from '../components/Gallery';
import Testimonials from '../components/Testimonials';

const HERO_IMAGE = "/images/HERO_IMAGE.jpg";
const HERO_LOGO = "/logo-white.png";
const VILLA_IMAGE = "/images/villa/06.jpg";
const GARDEN_IMAGE = "/images/GARDEN_IMAGE.jpg";
const GARDEN_IMAGE_02 = "/images/jardin/08.jpg";
const BEDROOM_IMAGE = "/images/villa/07.jpg";
const VILLA_IMAGE_08 = "/images/villa/08-2.jpg";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const amenities = [
  { icon: Waves, label: 'Alberca y Jacuzzi climatizados' },
  { icon: UtensilsCrossed, label: 'Cocina Equipada' },
  { icon: Mountain, label: 'Vista Panorámica' },
  { icon: Wifi, label: 'WiFi Alta Velocidad' },
  { icon: Car, label: 'Estacionamiento' },
  { icon: Beef, label: 'Asador tipo Argentino' },
];

const eventTypes = [
  { icon: Heart, label: 'Bodas', desc: 'El escenario perfecto para tu día especial' },
  { icon: Sparkles, label: 'XV Años', desc: 'Celebra en grande con un entorno mágico' },
  { icon: Users, label: 'Corporativos', desc: 'Reuniones y retiros empresariales' },
  { icon: PartyPopper, label: 'Fiestas', desc: 'Cumpleaños, bautizos y más' },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      {/* Altura minima (no fija) para que en celular el hero crezca con el
          contenido en vez de desbordarse por debajo del navbar */}
      <section className="relative min-h-screen">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
        </div>

        {/* pt-28 despeja el navbar fijo (h-20 = 80px); pb-28 despeja el
            indicador de scroll que va en absolute bottom-8 */}
        <div className="relative min-h-screen flex flex-col justify-center items-center text-center px-4 pt-28 pb-28 md:pt-24 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="font-serif-alt italic text-[#C8962D] text-lg md:text-xl mb-4">
              Tepic, Nayarit
            </p>
            {/* El logo sustituye al texto, pero se conserva el <h1> para no
                dejar la portada sin encabezado: el alt hace de titulo. */}
            <h1 className="mb-6">
              <img
                src={HERO_LOGO}
                alt="La Finca - Jardín y Villa"
                className="w-64 sm:w-80 lg:w-[24rem] max-w-full h-auto mx-auto"
              />
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Con la privacidad y belleza que da la naturaleza
            </p>
          </motion.div>

          {/* Booking Widget */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="w-full max-w-4xl px-4 mt-8"
          >
            <BookingWidget variant="hero" />
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Villa Section */}
      <section className="py-24 md:py-32 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
                Hospedaje
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mb-6">
                Villa de Descanso
              </h2>
              <p className="text-[#1A1A1A]/70 text-lg leading-relaxed mb-8">
                Disfruta de una estancia cómoda y relajante en Villa La Finca, ideal para compartir momentos especiales en familia o con amigos.

Nuestra villa tiene capacidad para hasta 10 huéspedes y cuenta con 3 recámaras, 3 baños completos, sala, comedor y cocina equipada. Además, podrás disfrutar de alberca, jacuzzi climatizado, terraza con vista a nuestros hermosos jardines y estacionamiento para 5 vehículos.

Un espacio diseñado para descansar, convivir y disfrutar de una experiencia inolvidable.
              </p>

              {/* Amenities Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
                {amenities.map((amenity) => (
                  <div
                    key={amenity.label}
                    className="flex items-center space-x-3 p-3 bg-white rounded-lg shadow-sm"
                  >
                    <amenity.icon className="h-5 w-5 text-[#C8962D]" />
                    <span className="text-sm text-[#1A1A1A]">{amenity.label}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/villa">
                  <Button
                    data-testid="villa-details-btn"
                    className="bg-[#5C4033] hover:bg-[#3D5A3E] text-white px-8"
                  >
                    Ver Detalles
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/reservar">
                  <Button
                    variant="outline"
                    data-testid="villa-reserve-btn"
                    className="border-[#5C4033] text-[#5C4033] hover:bg-[#5C4033] hover:text-white px-8"
                  >
                    Reservar Ahora
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="overflow-hidden rounded-2xl">
                    <img
                      src={VILLA_IMAGE}
                      alt="Villa Pool"
                      className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="overflow-hidden rounded-2xl">
                    <img
                      src={VILLA_IMAGE_08}
                      alt="Villa Bedroom"
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
                <div className="pt-8">
                  <div className="overflow-hidden rounded-2xl h-full">
                    <img
                      src={BEDROOM_IMAGE}
                      alt="Villa View"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
              </div>

              {/* Stats Card */}
              <div className="absolute -bottom-8 -left-8 bg-white p-6 rounded-2xl shadow-[0_8px_30px_rgba(92,64,51,0.15)]">
                <div className="grid grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="font-serif text-3xl text-[#5C4033]">10</p>
                    <p className="text-sm text-[#1A1A1A]/60">Huéspedes</p>
                  </div>
                  <div>
                    <p className="font-serif text-3xl text-[#5C4033]">3</p>
                    <p className="text-sm text-[#1A1A1A]/60">Recámaras</p>
                  </div>
                  <div>
                    <p className="font-serif text-3xl text-[#5C4033]">1,050</p>
                    <p className="text-sm text-[#1A1A1A]/60">m² área</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Garden Section */}
      <section className="py-24 md:py-32 bg-[#3D5A3E] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={GARDEN_IMAGE_02}
            alt="Garden Background"
            className="w-full h-full object-cover opacity-20"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
                Celebra con Nosotros
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-white mb-6">
                Jardín de Eventos
              </h2>
              <p className="text-white/80 text-lg max-w-2xl mx-auto">
                Haz de tu celebración un momento inolvidable en Jardín La Finca, un espacio rodeado de hermosos jardines, ideal para disfrutar y compartir tus eventos más especiales.<br></br><br></br>
              </p>
              <p className="text-white/80 text-lg max-w-2xl mx-auto">
                Contamos con capacidad para hasta 150 invitados, ofreciendo un ambiente agradable y versátil para bodas, cumpleaños, XV años, bautizos, reuniones familiares, eventos empresariales y celebraciones especiales.<br></br><br></br>
              </p>
              <p className="text-white/80 text-lg max-w-2xl mx-auto">
                Un lugar pensado para crear grandes momentos y recuerdos inolvidables junto a las personas que más quieres.
              </p>
            </motion.div>
          </div>

          {/* Event Types */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {eventTypes.map((event, index) => (
              <motion.div
                key={event.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center hover:bg-white/20 transition-colors"
              >
                <event.icon className="h-10 w-10 text-[#C8962D] mx-auto mb-4" />
                <h3 className="font-serif text-xl text-white mb-2">{event.label}</h3>
                <p className="text-white/70 text-sm">{event.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/jardin">
              <Button
                data-testid="garden-details-btn"
                className="bg-[#C8962D] hover:bg-[#B8861D] text-white px-8"
              >
                Conoce Nuestro Jardín
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="galeria" className="py-24 md:py-32 bg-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12 md:mb-16"
          >
            <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
              Nuestros Espacios
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mb-6">
              Galería
            </h2>
            <p className="text-[#1A1A1A]/70 text-lg max-w-2xl mx-auto">
              Descubre cada rincón de La Finca: nuestra villa, alberca, jacuzzi, terraza, asador y jardines, espacios creados para disfrutar, celebrar y vivir momentos inolvidables.
            </p>
          </motion.div>

          <Gallery limit={6} />

          <div className="text-center mt-12">
            <Link to="/galeria">
              <Button
                data-testid="gallery-more-btn"
                variant="outline"
                className="border-[#5C4033] text-[#5C4033] hover:bg-[#5C4033] hover:text-white px-8"
              >
                Ver Galería Completa
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <p className="font-serif-alt italic text-[#C8962D] text-lg mb-2">
              ¿Listo para tu escapada?
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1A1A1A] mb-6">
              Reserva Tu Estancia
            </h2>
            <p className="text-[#1A1A1A]/70 text-lg mb-8 max-w-2xl mx-auto">
              Escápate del ruido de la ciudad y disfruta de la tranquilidad que 
              solo La Finca puede ofrecerte. Reserva hoy y vive una experiencia única.
            </p>
            <Link to="/reservar">
              <Button
                data-testid="cta-reserve-btn"
                size="lg"
                className="bg-[#5C4033] hover:bg-[#3D5A3E] text-white px-12 py-6 text-lg"
              >
                Reservar Ahora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
